import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  connect() {
    this.form = this.element;
    window.validateFormData = (data) => this.validateFormData(data);

    for (const field of Array.from(this.form.elements)) {
      this.setExtraFieldAttributes(field);

      if (field.type !== 'submit') {
        field.addEventListener('input', () => this.validateSingleField(field));
      }

      // checkbox groups validate through their hidden field
      if (field.type === 'checkbox' && field.name.endsWith('_dummy')) {
        field.addEventListener('input', () => {
          if (typeof syncCheckboxGroups === 'function') syncCheckboxGroups();
          const hidden = document.getElementById(field.name.replace(/_dummy$/, ''));
          if (hidden?.getAttribute('data-validation-rules')) this.validateSingleField(hidden);
        });
      }
    }
  }

  interpolateI18n(template, vars) {
    return template.replace(/%\{(\w+)\}/g, (_, k) =>
      vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : '%{' + k + '}'
    );
  }

  fieldDisplayName(fieldKey) {
    const key = String(fieldKey);
    if (window.dynamicFormLocale !== 'en') {
      const label = (window.dynamicFormFieldLabels || {})[key];
      if (label) return label.toUpperCase();
    }
    return key.replace(/_/g, ' ').toUpperCase();
  }

  isBlankValue(value) {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    return String(value).trim() === '';
  }

  fieldHasRequiredRule(rules) {
    return rules.some((rule) => rule === 'required' || rule.startsWith('required_if:'));
  }

  skipOptionalBlankValidation(value, rules) {
    return this.isBlankValue(value) && !this.fieldHasRequiredRule(rules);
  }

  setExtraFieldAttributes(field) {
    const rulesAttribute = field.getAttribute('data-validation-rules');
    if (!rulesAttribute) return true;

    const rules = rulesAttribute.split(',');
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    for (const rule of rules) {
      if (rule.startsWith('max_date:')) {
        let max = rule.split(':')[1];
        if (max === 'today') max = `${year}-${month}-${day}`;
        document.getElementById(field.id)?.setAttribute('max', max);
      } else if (rule.startsWith('currency')) {
        field.addEventListener('input', () => this.formatAsCurrency(field));
        field.addEventListener('change', () => this.formatAsCurrency(field));
        this.formatAsCurrency(field);
      } else if (rule.startsWith('min_date:')) {
        let min = rule.split(':')[1];
        if (min === 'today') min = `${year}-${month}-${day}`;
        document.getElementById(field.id)?.setAttribute('min', min);
      }
    }
  }

  validateSingleField(field) {
    let value = null;

    if (field.type === 'checkbox') {
      const selectedCheckboxes = document.querySelectorAll(`input[name="${field.name}"]:checked`);
      value =
        selectedCheckboxes.length > 0 ? Array.from(selectedCheckboxes).map((cb) => cb.value) : null;
    } else {
      value = field.value;
    }

    const rulesAttribute = field.getAttribute('data-validation-rules');
    const errorMessageDataAttribute = field.getAttribute('errormessage') || '';

    if (!rulesAttribute) return true;

    const rules = rulesAttribute.split(',');
    let fieldIsValid = true;
    let errorMessage = '';
    const i18n = window.dynamicFormI18n || {};

    for (const rule of rules) {
      if (errorMessageDataAttribute !== '') errorMessage = errorMessageDataAttribute;

      if (rule === 'required' && !value) {
        fieldIsValid = false;
        if (errorMessage === '') errorMessage = i18n.this_field_required;
      } else if (rule.startsWith('min:')) {
        const min = Number.parseInt(rule.split(':')[1], 10);
        if ((value || '').length < min) {
          fieldIsValid = false;
          if (errorMessage === '')
            errorMessage = this.interpolateI18n(i18n.minimum_length, { min });
        }
      } else if (rule.startsWith('max:')) {
        const max = Number.parseInt(rule.split(':')[1], 10);
        if ((value || '').length > max) {
          fieldIsValid = false;
          if (errorMessage === '')
            errorMessage = this.interpolateI18n(i18n.maximum_length, { max });
          value = String(value).substring(0, max);
          field.value = value;
        }
      } else if (rule.startsWith('max_date:')) {
        if (!field.checkValidity()) {
          fieldIsValid = false;
          if (errorMessage === '') {
            errorMessage = this.interpolateI18n(i18n.max_date_error, {
              max: field.getAttribute('max'),
            });
          }
        }
      } else if (rule.startsWith('min_date:')) {
        if (!field.checkValidity()) {
          fieldIsValid = false;
          if (errorMessage === '') {
            errorMessage = this.interpolateI18n(i18n.min_date_error, {
              min: field.getAttribute('min'),
            });
          }
        }
      } else if (rule.startsWith('regex:')) {
        if (this.skipOptionalBlankValidation(value, rules)) continue;

        const formatRule = rule.split(':')[1];
        const cleanFormatRule = formatRule.replace(/COMMA/g, ',');
        const regex = new RegExp(cleanFormatRule);
        if (!regex.test(String(value || ''))) {
          fieldIsValid = false;
          if (errorMessage === '') errorMessage = i18n.please_enter_valid_format;
        }
      } else if (rule.startsWith('regex_if:')) {
        if (this.skipOptionalBlankValidation(value, rules)) continue;

        const format = rule.split(':')[1];
        const [condCombined, formatRule] = format.split('>');
        const [condField, condVal] = condCombined.split('=');
        const foundVal = document.getElementById(condField)?.value;

        if (condVal === foundVal) {
          const cleanFormatRule = formatRule.replace(/COMMA/g, ',');
          const regex = new RegExp(cleanFormatRule);
          if (!regex.test(String(value || ''))) {
            fieldIsValid = false;
            if (errorMessage === '') errorMessage = i18n.please_enter_valid_format;
          }
        }
      } else if (rule.startsWith('format:')) {
        field.value = String(value || '').toUpperCase();
      } else if (rule.startsWith('required_if:') && (!value || value === '')) {
        const spec = rule.split(':')[1];
        const checkFieldName = spec.split('=')[0];
        const checkValues = spec.split('=')[1].split('|');
        const checkFieldValue = document.getElementById(checkFieldName)?.value;

        if (checkValues.includes(checkFieldValue)) {
          fieldIsValid = false;
          if (errorMessage === '') {
            errorMessage = this.interpolateI18n(i18n.required_if_error, {
              field: this.fieldDisplayName(checkFieldName),
              values: checkValues.join(', '),
            });
          }
        }
      } else if (rule.startsWith('lesser_than:') || rule.startsWith('lesser_than_eq:')) {
        const maxFieldName = rule.split(':')[1];
        const maxField = document.getElementById(maxFieldName);
        const max = maxField?.value || '';
        const lesser = field.value || '';

        if (parseInt(lesser.replace(/,/g, ''), 10) > parseInt(max.replace(/,/g, ''), 10)) {
          fieldIsValid = false;
          if (errorMessage === '') {
            errorMessage = this.interpolateI18n(i18n.should_not_be_higher_than, {
              field: this.fieldDisplayName(maxFieldName),
            });
          }
        }
      } else if (rule.startsWith('not_the_same_as:')) {
        if (this.skipOptionalBlankValidation(field.value, rules)) continue;

        const otherFieldName = rule.split(':')[1];
        const otherField = document.getElementById(otherFieldName);
        if (field.value === otherField?.value) {
          fieldIsValid = false;
          if (errorMessage === '') {
            errorMessage = this.interpolateI18n(i18n.should_not_be_same_as, {
              field: this.fieldDisplayName(otherFieldName),
            });
          }
        }
      } else if (rule.startsWith('either_required:')) {
        const otherFieldName = rule.split(':')[1];
        const otherField = document.getElementById(otherFieldName);
        if (!field.value && !otherField?.value) {
          fieldIsValid = false;
          errorMessage = this.interpolateI18n(i18n.either_required, {
            field: this.fieldDisplayName(otherFieldName),
          });
        }
      }
    }

    if (fieldIsValid) this.clearError(field);
    else this.showError(field, errorMessage);

    return fieldIsValid;
  }

  validateFormData(formData) {
    let isFormValid = true;
    let firstInvalidField = null;

    for (const fieldName of Object.keys(formData)) {
      const fields = document.getElementsByName(fieldName);
      let field = fields.length > 0 ? fields[0] : document.getElementById(fieldName);

      if (field && !this.validateSingleField(field)) {
        isFormValid = false;
        if (!firstInvalidField) firstInvalidField = field;
      }
    }

    if (firstInvalidField) firstInvalidField.focus();
    return isFormValid;
  }

  showError(field, message) {
    const errorDiv = document.getElementById(`${field.id}_error`);
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.classList.remove('hidden');
    }
    (field.wrapper_field || field).classList.add(
      'border-pink',
      'ring',
      'ring-pink',
      'ring-opacity-50'
    );
  }

  clearError(field) {
    const errorDiv = document.getElementById(`${field.id}_error`);
    if (errorDiv) errorDiv.classList.add('hidden');
    (field.wrapper_field || field).classList.remove(
      'border-pink',
      'ring',
      'ring-pink',
      'ring-opacity-50'
    );
  }

  formatAsCurrency(field) {
    const hiddenField = document.getElementById(field.dataset.currencyHiddenFieldId);
    if (!hiddenField) return;

    const currency = field.dataset.currencyCode || 'USD';
    const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
    const decimalSep = formatter.formatToParts(1.1).find((p) => p.type === 'decimal')?.value || '.';
    const maxDecimals = formatter.resolvedOptions().maximumFractionDigits;

    const allowedChars = `0-9${maxDecimals > 0 ? decimalSep : ''}`;
    const cleaned = field.value.replace(new RegExp(`[^${allowedChars}]`, 'g'), '');

    if (!cleaned) {
      field.value = '';
      hiddenField.value = '';
      return;
    }

    const [rawInt = '', ...rest] = cleaned.split(decimalSep);
    const decimals = rest.join('').slice(0, maxDecimals);
    const hasSep = cleaned.includes(decimalSep) && maxDecimals > 0;
    const intPart = rawInt.replace(/^0+(?=\d)/, '') || '0';
    const intDisplay = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
      Number(intPart)
    );

    field.value = hasSep ? `${intDisplay}${decimalSep}${decimals}` : intDisplay;
    hiddenField.value = hasSep && decimals ? `${intPart}.${decimals}` : intPart;
  }
}
